import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, KeyRound, ShieldCheck, Eye, EyeOff, ChevronLeft, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../common/context/AuthContext';
import Toast from '../../common/components/Toast';
import { API_URL } from '../../api/config';

export default function LoginMethodSelection() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn: authSignIn } = useAuth();

  const { 
    email, 
    restaurantId, 
    restaurantName, 
    outletName, 
    availableMethods,
    passwordProvided,
    token: initialToken,
    user: initialUser,
    isOutletSwitch // NEW: Check if this is outlet switch
  } = location.state || {};

  const [loginMethod, setLoginMethod] = useState(null);
  const [credential, setCredential] = useState('');
  const [pinInput, setPinInput] = useState(['', '', '', '']);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    if (!email || !restaurantId || !availableMethods) {
      navigate('/signin');
      return;
    }
    
    // KEY FIX: NEVER auto-login if this is an outlet switch
    // Only allow auto-login for regular login flow, not outlet switches
    if (passwordProvided && initialToken && initialUser && !isOutletSwitch) {
      setLoginMethod('password');
    }
  }, [email, restaurantId, availableMethods, navigate, passwordProvided, initialToken, initialUser, isOutletSwitch]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  const handlePinInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...pinInput];
    newPin[index] = value.slice(-1);
    setPinInput(newPin);
    setError('');

    if (value && index < 3) {
      const nextInput = document.getElementById(`pin-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    if (index === 3 && value && newPin.every(d => d)) {
      handleLogin(newPin.join(''));
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      const prevInput = document.getElementById(`pin-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleLogin = async (pinValue = null) => {
    // KEY FIX: NEVER use cached token for outlet switches
    // If password was already validated and method is password, AND NOT outlet switch, use existing token
    if (loginMethod === 'password' && passwordProvided && initialToken && initialUser && !isOutletSwitch) {
      try {
        await authSignIn({ token: initialToken, user: initialUser });
        showToast('Signed in successfully');
        navigate('/dashboard');
        return;
      } catch (error) {
        setError('Session expired. Please try again.');
        return;
      }
    }

    let credentialValue = loginMethod === 'password' ? credential : (pinValue || pinInput.join(''));
    let credentialType = loginMethod;

    if (!credentialValue) {
      setError(loginMethod === 'password' ? 'Please enter your password' : 'Please enter your PIN');
      return;
    }

    if (loginMethod !== 'password' && credentialValue.length !== 4) {
      setError('Please enter a complete 4-digit PIN');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post(`${API_URL}/auth/unified-login`, {
        email,
        restaurantId,
        credential: credentialValue,
        credentialType
      });

      if (response.data.success) {
        const { token, user, isAdminSession, outlets } = response.data;
        await authSignIn({ token, user });
        showToast('Signed in successfully');

        // Admin PIN: go to outlet picker so they can choose which outlet to enter
        if (isAdminSession && outlets?.length > 1) {
          navigate('/outlet-selection', {
            replace: true,
            state: {
              outlets,
              email: user.email,
              isAdminSession: true  // skip PIN on outlet switch
            }
          });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid credentials');
      showToast(error.response?.data?.message || 'Invalid credentials', 'error');
      if (loginMethod !== 'password') {
        setPinInput(['', '', '', '']);
        document.getElementById('pin-0')?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const getMethodIcon = (method) => {
    switch(method) {
      case 'password':
        return <Lock size={24} />;
      case 'outlet_pin':
        return <KeyRound size={24} />;
      case 'admin_pin':
        return <ShieldCheck size={24} />;
      default:
        return <Lock size={24} />;
    }
  };

  const getMethodColor = (method) => {
    switch(method) {
      case 'password':
        return 'blue';
      case 'outlet_pin':
        return 'green';
      case 'admin_pin':
        return 'purple';
      default:
        return 'blue';
    }
  };

  const getMethodTitle = (method) => {
    switch(method) {
      case 'password':
        return 'Password';
      case 'outlet_pin':
        return 'Outlet PIN';
      case 'admin_pin':
        return 'Admin PIN';
      default:
        return 'Login';
    }
  };

  const getMethodDescription = (method) => {
    switch(method) {
      case 'password':
        return 'Use your account password';
      case 'outlet_pin':
        return '4-digit quick access PIN';
      case 'admin_pin':
        return 'Administrator access PIN';
      default:
        return '';
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      
      {/* Header */}
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-blue-900">TOBO</h2>
          <span className="text-sm font-semibold text-blue-700">BILL IT, DONE!</span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <button
            onClick={() => navigate(isOutletSwitch ? '/dashboard' : '/signin')}
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors mb-6"
          >
            <ChevronLeft size={18} />
            <span className="text-sm font-medium">
              {isOutletSwitch ? 'Cancel Switch' : 'Back to Sign In'}
            </span>
          </button>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
            {/* Outlet Info Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-8 text-white">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
                <ShieldCheck size={32} />
              </div>
              <h1 className="text-2xl font-bold mb-2">
                {isOutletSwitch ? 'Switch to Outlet' : 'Choose Login Method'}
              </h1>
              <p className="text-blue-100 text-sm font-medium">{restaurantName}</p>
              {outletName && <p className="text-blue-200 text-xs">{outletName}</p>}
              {isOutletSwitch && (
                <div className="mt-4 p-3 bg-yellow-500/20 border border-yellow-400/30 rounded-lg">
                  <p className="text-xs text-yellow-100 font-medium">
                    🔒 Authentication required to switch outlets
                  </p>
                </div>
              )}
            </div>

            {/* Login Method Selection or Input */}
            <div className="p-8">
              {!loginMethod ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 font-medium mb-6">
                    Select your preferred login method:
                  </p>

                  {/* Password Option */}
                  {availableMethods?.password && (
                    <button
                      onClick={() => setLoginMethod('password')}
                      className="w-full p-5 border-2 border-slate-200 rounded-2xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 group-hover:bg-blue-200 rounded-xl flex items-center justify-center transition-colors">
                          <Lock className="text-blue-600" size={24} />
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="font-bold text-slate-900 mb-1">Password</h3>
                          <p className="text-xs text-slate-500">Use your account password</p>
                        </div>
                        <div className="text-slate-300 group-hover:text-blue-600 transition-colors">
                          <ChevronLeft size={20} className="rotate-180" />
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Outlet PIN Option */}
                  {availableMethods?.outletPin && (
                    <button
                      onClick={() => setLoginMethod('outlet_pin')}
                      className="w-full p-5 border-2 border-slate-200 rounded-2xl hover:border-green-500 hover:bg-green-50 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 group-hover:bg-green-200 rounded-xl flex items-center justify-center transition-colors">
                          <KeyRound className="text-green-600" size={24} />
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="font-bold text-slate-900 mb-1">Outlet PIN</h3>
                          <p className="text-xs text-slate-500">4-digit quick access PIN</p>
                        </div>
                        <div className="text-slate-300 group-hover:text-green-600 transition-colors">
                          <ChevronLeft size={20} className="rotate-180" />
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Admin PIN Option */}
                  {availableMethods?.adminPin && (
                    <button
                      onClick={() => setLoginMethod('admin_pin')}
                      className="w-full p-5 border-2 border-slate-200 rounded-2xl hover:border-purple-500 hover:bg-purple-50 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 group-hover:bg-purple-200 rounded-xl flex items-center justify-center transition-colors">
                          <ShieldCheck className="text-purple-600" size={24} />
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="font-bold text-slate-900 mb-1">Admin PIN</h3>
                          <p className="text-xs text-slate-500">Administrator access PIN</p>
                        </div>
                        <div className="text-slate-300 group-hover:text-purple-600 transition-colors">
                          <ChevronLeft size={20} className="rotate-180" />
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Back to Methods */}
                  <button
                    onClick={() => {
                      setLoginMethod(null);
                      setCredential('');
                      setPinInput(['', '', '', '']);
                      setError('');
                    }}
                    className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    <ChevronLeft size={16} />
                    Change login method
                  </button>

                  {/* Login Title */}
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-slate-900">
                      Enter {getMethodTitle(loginMethod)}
                    </h2>
                    <p className="text-sm text-slate-500 mt-2">
                      {getMethodDescription(loginMethod)}
                    </p>
                  </div>

                  {/* Password Input */}
                  {loginMethod === 'password' && (
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={credential}
                          onChange={(e) => {
                            setCredential(e.target.value);
                            setError('');
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                          placeholder="Enter your password"
                          className="w-full pl-12 pr-12 py-4 border-2 border-slate-200 rounded-xl text-base font-semibold focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none transition-all"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600"
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* PIN Input */}
                  {loginMethod !== 'password' && (
                    <div className="space-y-4">
                      <label className="block text-sm font-semibold text-slate-700 text-center">
                        Enter 4-digit PIN
                      </label>
                      <div className="flex justify-center gap-3">
                        {pinInput.map((digit, index) => (
                          <input
                            key={index}
                            id={`pin-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handlePinInputChange(index, e.target.value)}
                            onKeyDown={(e) => handlePinKeyDown(index, e)}
                            disabled={loading}
                            className={`w-16 h-16 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                              error 
                                ? 'border-red-500 focus:ring-red-500' 
                                : 'border-slate-300 focus:ring-blue-500 focus:border-blue-500'
                            } disabled:opacity-50`}
                            autoFocus={index === 0}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl">
                      <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
                      <p className="text-sm font-medium text-red-700">{error}</p>
                    </div>
                  )}

                  {/* Login Button */}
                  <button
                    onClick={() => handleLogin()}
                    disabled={loading || (loginMethod === 'password' && !credential) || (loginMethod !== 'password' && pinInput.some(d => !d))}
                    className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all transform active:scale-95 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Signing in...' : isOutletSwitch ? 'Switch Outlet' : 'Sign In'}
                  </button>

                  {/* Forgot Password Link */}
                  {loginMethod === 'password' && !isOutletSwitch && (
                    <div className="text-center">
                      <button
                        onClick={() => navigate('/forgot-password', { state: { email, restaurantId } })}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4">
            <p className="text-xs text-blue-900 font-medium leading-relaxed text-center">
              🔒 Your credentials are encrypted and secured
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 text-center border-t border-slate-200 bg-white">
        <p className="text-xs text-slate-500 uppercase font-semibold">
          © 2024 TOBO Inc. • All Rights Reserved
        </p>
      </footer>
    </div>
  );
}